import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ExpandableRichText from '../ExpandableRichText/ExpandableRichText';
import { getCharacterProfilePath } from '../../utils/characterPaths';
import styles from './FactionMemberCard.module.css';

const FactionMemberCard = ({
  member,
  memberType = 'character',
  factionCanEdit = false,
  editingRoleFor = null,
  roleDraft = '',
  onRoleDraftChange,
  onStartRoleEdition,
  onCancelRoleEdition,
  onSaveRoleEdition,
  actionLoading = false,
  onEditNote,
  onLeave,
}) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const isCharacter = memberType === 'character';
  const profilePath = isCharacter ? getCharacterProfilePath(member) : null;
  const isEditingRole = isCharacter && editingRoleFor === member.id;

  const hasNote = Boolean(member.memberNote?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim());
  const leaveLabel = member.isMine ? 'Quitter la faction' : 'Retirer de la liste';

  const handleLeave = () => {
    const message = member.isMine
      ? 'Quitter cette faction avec ce personnage ?'
      : `Retirer ${member.name} de la faction ?`;
    if (window.confirm(message)) {
      onLeave(member.id);
    }
  };

  return (
    <article className={styles.card}>
      <div className={styles.header}>
        <div className={styles.avatar}>
          {member.avatar ? (
            <img src={member.avatar} alt="" />
          ) : (
            <span className={styles.avatarFallback}>
              {(member.name || '?').charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        <div className={styles.identity}>
          {profilePath ? (
            <h3 className={styles.name}>
              <Link to={profilePath} className={styles.nameLink}>{member.name}</Link>
            </h3>
          ) : (
            <h3 className={styles.name}>{member.name}</h3>
          )}

          {member.user?.pseudo && (
            <p className={styles.meta}>Joueur : {member.user.pseudo}</p>
          )}
          {!isCharacter && member.occupation && (
            <p className={styles.meta}>{member.occupation}</p>
          )}

          {isCharacter && (
            isEditingRole && factionCanEdit ? (
              <div className={styles.roleEditor}>
                <input
                  className={styles.roleInput}
                  value={roleDraft}
                  onChange={(event) => onRoleDraftChange(event.target.value)}
                  placeholder="Ex: bras droit, éclaireur..."
                />
                <button
                  type="button"
                  className={styles.roleAction}
                  onClick={() => onSaveRoleEdition(member.id)}
                  disabled={actionLoading}
                >
                  OK
                </button>
                <button type="button" className={styles.roleActionMuted} onClick={onCancelRoleEdition}>
                  Annuler
                </button>
              </div>
            ) : (
              <p
                className={`${styles.roleLine} ${factionCanEdit ? styles.roleLineEditable : ''}`}
                onClick={factionCanEdit && !isEditingRole ? () => onStartRoleEdition(member.id, member.roleRp) : undefined}
                onKeyDown={factionCanEdit && !isEditingRole ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onStartRoleEdition(member.id, member.roleRp);
                  }
                } : undefined}
                role={factionCanEdit ? 'button' : undefined}
                tabIndex={factionCanEdit ? 0 : undefined}
              >
                <span className={styles.roleLabel}>Rôle RP</span>
                <span className={styles.roleValue}>
                  {member.roleRp?.trim() || (factionCanEdit ? 'Cliquer pour définir' : 'Non défini')}
                </span>
              </p>
            )
          )}
        </div>

        <div className={styles.actions}>
          {member.canEditMemberNote && (
            <button
              type="button"
              className={styles.iconAction}
              onClick={() => onEditNote(member)}
              aria-label={hasNote ? 'Modifier les informations' : 'Ajouter des informations'}
              title={hasNote ? 'Modifier les informations' : 'Ajouter des informations'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {member.canLeave && (
            <button
              type="button"
              className={`${styles.iconAction} ${styles.iconActionDanger}`}
              onClick={handleLeave}
              disabled={actionLoading}
              aria-label={leaveLabel}
              title={leaveLabel}
            >
              {member.isMine ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="17" y1="11" x2="23" y2="11" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {hasNote && (
        <div className={styles.accordion}>
          <div className={styles.accordionItem}>
            <button
              type="button"
              className={styles.accordionTrigger}
              onClick={() => setNoteOpen((prev) => !prev)}
              aria-expanded={noteOpen}
            >
              <span>Informations dans la faction</span>
              <svg
                className={`${styles.accordionChevron} ${noteOpen ? styles.accordionChevronOpen : ''}`}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {noteOpen && (
              <div className={styles.accordionPanel}>
                <ExpandableRichText
                  html={member.memberNote}
                  collapsedLines={6}
                  emptyMessage="Aucune information pour le moment."
                />
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

export default FactionMemberCard;
